import * as Y from "yjs";
import { entityTypes, type EntityType } from "../schemas";
import { changesetField, changesetsMapName, entityMap, jsonEqual, type ChangeOrigin } from "./doc";
import { runChange, type DocWriter } from "./writer";

interface FieldChange {
  type: EntityType;
  id: string;
  field: string;
  /** Undefined when the field (or entity) didn't exist before. */
  before: unknown;
  existed: boolean;
  createdEntity: boolean;
  /** What the change left there (the whole entity, for a created one); filled in afterwards. */
  after?: unknown;
}

export interface UndoOutcome {
  /** True when anything was restored. */
  undone: boolean;
  /** Fields (or created entities) left alone because they were changed again since. */
  skipped: number;
}

export interface Undoable<R> {
  result: R;
  /** Restores every field the change wrote, as a new changeset. False if nothing to undo. */
  undo(origin: ChangeOrigin): boolean;
  /**
   * Like `undo`, but reports how many fields were skipped: a field whose value is no longer
   * what the change wrote was edited since (here or on another device) and is left alone.
   */
  undoWithReport(origin: ChangeOrigin): UndoOutcome;
}

function entityJson(entity: Y.Map<unknown>): Record<string, unknown> {
  const json = entity.toJSON() as Record<string, unknown>;
  delete json[changesetField];
  return json;
}

/**
 * Runs a command and records the previous value of every field it wrote, so the change
 * can be undone (e.g. "Saved — Undo"). The undo is itself a recorded changeset.
 */
export function runUndoable<R>(
  doc: Y.Doc,
  origin: ChangeOrigin,
  fn: (w: DocWriter) => R,
): Undoable<R> {
  const changes: FieldChange[] = [];
  const handlers = entityTypes.map((type) => {
    const top = entityMap(doc, type);
    const handler = (events: Y.YEvent<Y.Map<unknown>>[], tr: Y.Transaction) => {
      if (tr.origin !== origin) {
        return;
      }
      for (const event of events) {
        if (event.target === top) {
          event.changes.keys.forEach((change, id) => {
            if (change.action === "add") {
              changes.push({
                type,
                id,
                field: "",
                before: undefined,
                existed: false,
                createdEntity: true,
              });
            }
          });
        } else if (event.path.length === 1) {
          const id = String(event.path[0]);
          event.changes.keys.forEach((change, field) => {
            if (field !== changesetField) {
              changes.push({
                type,
                id,
                field,
                before: change.oldValue,
                existed: change.action !== "add",
                createdEntity: false,
              });
            }
          });
        }
      }
    };
    top.observeDeep(handler);
    return () => top.unobserveDeep(handler);
  });
  let result: R;
  try {
    result = runChange(doc, origin, fn);
  } finally {
    for (const off of handlers) {
      off();
    }
  }
  // One entry per field: the earliest previous value wins for fields written twice.
  const created = new Set(changes.filter((c) => c.createdEntity).map((c) => `${c.type}:${c.id}`));
  const unique = new Map<string, FieldChange>();
  for (const c of changes) {
    const entityKey = `${c.type}:${c.id}`;
    if (!c.createdEntity && created.has(entityKey)) {
      continue;
    }
    const key = `${entityKey}:${c.field}`;
    if (!unique.has(key)) {
      unique.set(key, c);
    }
  }
  for (const c of unique.values()) {
    const entity = entityMap(doc, c.type).get(c.id);
    if (entity instanceof Y.Map) {
      c.after = c.createdEntity ? entityJson(entity) : entity.get(c.field);
    }
  }

  const undoWithReport = (undoOrigin: ChangeOrigin): UndoOutcome => {
    let undone = false;
    let skipped = 0;
    doc.transact(() => {
      for (const c of unique.values()) {
        const entity = entityMap(doc, c.type).get(c.id);
        if (!(entity instanceof Y.Map)) {
          continue;
        }
        const now = c.createdEntity ? entityJson(entity) : entity.get(c.field);
        if (!jsonEqual(now, c.after)) {
          skipped++;
          continue;
        }
        if (c.createdEntity) {
          entity.set("deleted", true);
        } else if (c.existed) {
          entity.set(c.field, c.before);
        } else {
          entity.delete(c.field);
        }
        entity.set(changesetField, undoOrigin.changesetId);
        undone = true;
      }
      if (undone) {
        const { changesetId, ...meta } = undoOrigin;
        doc.getMap(changesetsMapName).set(changesetId, meta);
      }
    }, undoOrigin);
    return { undone, skipped };
  };

  return {
    result,
    undo: (undoOrigin) => undoWithReport(undoOrigin).undone,
    undoWithReport,
  };
}
